// Tasks, notes, calls and meetings (decision 01.11.2026). "Meine Aufgaben" (#aufgaben) lists the open tasks of the
// signed-in user, or all open ones: overdue first, then today, this week, later. The deal drawer, the company page and
// the inquiries share the small task list (add, done, change due date and assignee) and the buttons Notiz, Anruf,
// Termin that record what happened. The deal's earliest open task is its next step.
import type { ActivityTarget, Attendee, ContactOption, NewActivity, Task } from "../api";
import { loadTasks, render, S, write } from "../state";
import { $, emptyState, esc, fmtDT, today, toast } from "../util";

type Group = "overdue" | "today" | "week" | "later";
const GROUP_TITLE: Record<Group, string> = { overdue: "Überfällig", today: "Heute", week: "Diese Woche", later: "Später" };
const SOURCE: Record<Task["source"], string> = { manual: "", hubspot: "aus HubSpot", ai: "Vorschlag der KI", ange_bot: "von Ange.Bot" };

const addDays = (iso: string, n: number) => new Date(Date.parse(`${iso}T12:00:00Z`) + n * 864e5).toISOString().slice(0, 10);
/** The Sunday ending the week of `day` (weeks start on Monday). */
export const endOfWeek = (day: string) => addDays(day, (7 - new Date(`${day}T12:00:00Z`).getUTCDay()) % 7);

/** Where a task falls: undated ones count as later. */
export function taskGroup(due: string | null, day = today()): Group {
  if (!due || due > endOfWeek(day)) return "later";
  return due < day ? "overdue" : due === day ? "today" : "week";
}

/** "28.10." in the current year, "28.10.25" before; a bare date is taken at noon so no time zone shifts the day. */
function day(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", ...(d.getFullYear() === new Date().getFullYear() ? {} : { year: "2-digit" }) });
}

/** "fällig 03.11.", red when overdue. */
const dueText = (t: TaskLike) => (t.due ? `<span class="due mono ${t.due < today() ? "over" : ""}">${t.due < today() ? "überfällig seit" : "fällig"} ${day(t.due)}</span>` : "");

/** What a task list needs of a task; where it belongs only where it is shown. */
type TaskLike = Pick<Task, "id" | "version" | "text" | "due" | "assignee" | "source"> & Partial<Pick<Task, "deal" | "company" | "inquiry">>;

/** The way to where the task belongs. */
function whereLink(t: TaskLike): string {
  if (t.deal) return `<a href="#deal-${esc(t.deal.id)}"><span class="mono">${esc(t.deal.ref)}</span> ${esc(t.company?.name ?? "")} · ${esc(t.deal.name)}</a>${t.deal.closed ? ` <span class="muted">(abgeschlossen)</span>` : ""}`;
  if (t.inquiry) return `<a href="#anfragen">Anfrage ${esc(t.inquiry.label)}</a>`;
  if (t.company) return `<a href="#firma-${esc(t.company.id)}">${esc(t.company.name)}</a>`;
  return "";
}

const assigneeOptions = (selected: string | null) =>
  `<option value="">Niemand</option>${S.users.map((u) => `<option value="${esc(u.id)}" ${u.id === selected ? "selected" : ""}>${esc(u.name)}</option>`).join("")}`;

/**
 * One open task with "Erledigt"; with `where`, the deal, company or inquiry it belongs to; with `edit`, its due date
 * and assignee to change, else who has it.
 */
function taskRow(t: TaskLike, opts: { where: boolean; edit: boolean }): string {
  const ro = S.readOnly;
  const meta = [SOURCE[t.source], opts.where ? whereLink(t) : ""].filter(Boolean).join(" · ");
  const controls =
    opts.edit && !ro
      ? `<span class="tk-ctl"><input type="date" data-task-due value="${esc(t.due ?? "")}" aria-label="Fällig am"><select data-task-assignee aria-label="Zuständig">${assigneeOptions(t.assignee?.id ?? null)}</select></span>`
      : `<span class="muted">${esc(t.assignee?.name ?? "Niemand zugewiesen")}</span>`;
  return `<li class="next tk" data-task="${esc(t.id)}" data-version="${t.version}"><div class="tk-b"><div>${esc(t.text)}</div>
      <div class="tk-m">${dueText(t)}${meta ? ` <span class="muted">${meta}</span>` : ""}${controls ? ` ${controls}` : ""}</div></div>
    <button class="btn" data-task-done ${ro ? "disabled" : ""}>Erledigt</button></li>`;
}

/** The task list of a deal, company or inquiry with the form for a new one. */
export function taskBox(target: ActivityTarget, tasks: TaskLike[], opts: { where?: boolean; empty?: string } = {}): string {
  const list = tasks.length
    ? `<ul class="tasks">${tasks.map((t) => taskRow(t, { where: !!opts.where, edit: true })).join("")}</ul>`
    : `<p class="muted" style="margin:0">${esc(opts.empty ?? "Keine offenen Aufgaben.")}</p>`;
  const form = S.readOnly
    ? ""
    : `<form class="nextform taskform" data-task-add='${esc(JSON.stringify(target))}'><input name="text" placeholder="Neue Aufgabe" maxlength="300" aria-label="Neue Aufgabe">
      <input name="due" type="date" aria-label="Fällig am"><select name="assigneeId" aria-label="Zuständig">${assigneeOptions(S.me?.id ?? null)}</select><button class="btn">Hinzufügen</button></form>`;
  return list + form;
}

/**
 * The details of a call or meeting in the history: direction and minutes; time, place, participants, called off; for an
 * Outlook meeting our people, the Teams link and the own calendar's link, or that it is still being created.
 */
export function activityDetails(a: {
  type: string;
  direction: "in" | "out" | null;
  durationMin: number | null;
  endsAt: string | null;
  location: string | null;
  attendees: Attendee[];
  cancelled: boolean;
  internalAttendees?: { name: string | null; email: string }[];
  calendars?: { owner: string; webLink: string | null }[];
  onlineUrl?: string | null;
  calendarState?: "pending" | "created" | "failed" | null;
  calendarError?: string | null;
}): string {
  const parts: string[] = [];
  if (a.type === "call" && a.direction) parts.push(a.direction === "in" ? "Kunde hat angerufen" : "Wir haben angerufen");
  if (a.type === "call" && a.durationMin) parts.push(`${a.durationMin} min`);
  if (a.type === "meeting" && a.endsAt) parts.push(`bis ${fmtDT(a.endsAt)}`);
  if (a.location) parts.push(esc(a.location));
  const who = a.attendees.map((p) => p.name || p.email).filter(Boolean);
  if (who.length) parts.push(`mit ${esc(who.join(", "))}`);
  const ours = (a.internalAttendees ?? []).map((p) => p.name || p.email);
  if (ours.length) parts.push(`von uns ${esc(ours.join(", "))}`);
  const mine = a.calendars?.find((c) => c.owner.toLowerCase() === (S.me?.email ?? "").toLowerCase())?.webLink;
  const links = [a.onlineUrl ? `<a href="${esc(a.onlineUrl)}" target="_blank" rel="noopener">Teams</a>` : "", mine ? `<a href="${esc(mine)}" target="_blank" rel="noopener">In Outlook öffnen</a>` : ""].filter(Boolean).join(" · ");
  const state =
    a.calendarState === "pending" ? `<p class="note" style="margin:4px 0 0">Wird in Outlook angelegt …</p>` : a.calendarState === "failed" ? `<p class="note warn" style="margin:4px 0 0">Nicht in Outlook angelegt: ${esc(a.calendarError ?? "")}</p>` : "";
  return `${a.cancelled ? `<span class="pill p-info">abgesagt</span> ` : ""}${parts.length ? `<div class="muted act-m">${parts.join(" · ")}</div>` : ""}${links ? `<div class="act-m">${links}</div>` : ""}${state}`;
}

/** The form being filled to record a note, call or meeting, or a meeting for Outlook, kept across a redraw of the page. */
type RecordKind = "note" | "call" | "meeting" | "outlook";
let recording: { key: string; kind: RecordKind; draft: Record<string, string> } | null = null;
const keyOf = (t: ActivityTarget) => JSON.stringify(t);

/** A date and time for an input field, local time. */
const localInput = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);

/**
 * "Notiz", "Anruf", "Termin" and the open form; `contacts`: whom a meeting can have as participants (the company's
 * contacts that are not hidden). `outlook`: at a deal whose user has a connected calendar, "Termin in Outlook anlegen"
 * with the deal's contacts that have an address. The box tells the handlers where it records.
 */
export function recordBox(target: ActivityTarget, contacts: ContactOption[], outlook?: ContactOption[]): string {
  if (S.readOnly) return "";
  const open = recording?.key === keyOf(target) ? recording : null;
  const v = (name: string, fallback = "") => esc(open?.draft[name] ?? fallback);
  const btn = (kind: RecordKind, label: string) => `<button type="button" class="btn" data-record="${kind}" aria-pressed="${open?.kind === kind}">${label}</button>`;
  const now = localInput(new Date());
  let form = "";
  if (open?.kind === "note") {
    form = `<label><span>Notiz</span><textarea name="body" rows="4" required maxlength="10000">${v("body")}</textarea></label><label><span>Zeitpunkt</span><input name="at" type="datetime-local" value="${v("at", now)}"></label>`;
  } else if (open?.kind === "call") {
    const dir = open.draft.direction ?? "out";
    form = `<label><span>Richtung</span><select name="direction"><option value="out" ${dir === "out" ? "selected" : ""}>Wir haben angerufen</option><option value="in" ${dir === "in" ? "selected" : ""}>Kunde hat angerufen</option></select></label>
      <label><span>Zeitpunkt</span><input name="at" type="datetime-local" value="${v("at", now)}"></label>
      <label><span>Dauer (Minuten)</span><input name="durationMin" type="number" min="0" max="1440" value="${v("durationMin")}"></label>
      <label><span>Betreff</span><input name="subject" maxlength="300" value="${v("subject")}"></label>
      <label><span>Notiz zum Gespräch</span><textarea name="body" rows="3" maxlength="10000">${v("body")}</textarea></label>`;
  } else if (open?.kind === "meeting") {
    const picked = new Set((open.draft.contacts ?? "").split(",").filter(Boolean));
    form = `<label class="wide"><span>Betreff</span><input name="subject" required maxlength="300" value="${v("subject")}"></label>
      <label><span>Beginn</span><input name="at" type="datetime-local" required value="${v("at", now)}"></label>
      <label><span>Ende</span><input name="endsAt" type="datetime-local" value="${v("endsAt")}"></label>
      <label class="wide"><span>Ort</span><input name="location" maxlength="300" value="${v("location")}" placeholder="z. B. beim Kunden, Teams, Telefon"></label>
      ${contacts.length ? `<fieldset class="wide picks"><legend>Teilnehmer</legend>${contacts.map((c) => `<label class="sel"><input type="checkbox" name="contact" value="${esc(c.id)}" ${picked.has(c.id) ? "checked" : ""}> ${esc(c.name ?? c.email ?? "Ohne Namen")}</label>`).join("")}</fieldset>` : ""}
      <label class="wide"><span>Weitere Teilnehmer (Name oder Mail, durch Komma getrennt)</span><input name="others" maxlength="2000" value="${v("others")}"></label>
      <label><span>Notiz</span><textarea name="body" rows="3" maxlength="10000">${v("body")}</textarea></label>`;
  } else if (open?.kind === "outlook") {
    const picked = new Set((open.draft.contacts ?? "").split(",").filter(Boolean));
    const later = localInput(new Date(Date.now() + 86_400_000)).slice(0, 11) + "10:00";
    form = `<p class="note wide" style="margin:0">Der Termin entsteht in deinem Outlook-Kalender, du bist Organisator; der Betreff trägt die Vorgangsnummer. Er steht gleich hier am Deal.</p>
      <label class="wide"><span>Betreff</span><input name="subject" required maxlength="250" value="${v("subject")}"></label>
      <label><span>Beginn</span><input name="at" type="datetime-local" required value="${v("at", later)}"></label>
      <label><span>Ende</span><input name="endsAt" type="datetime-local" required value="${v("endsAt")}"></label>
      <label class="wide"><span>Ort</span><input name="location" maxlength="300" value="${v("location")}"></label>
      ${outlook?.length ? `<fieldset class="wide picks"><legend>Einladen</legend>${outlook.map((c) => `<label class="sel"><input type="checkbox" name="contact" value="${esc(c.id)}" ${picked.has(c.id) ? "checked" : ""}> ${esc(c.name ?? c.email ?? "")} <span class="muted mono">${esc(c.email ?? "")}</span></label>`).join("")}</fieldset>` : `<p class="muted wide" style="margin:0">Keine Ansprechpartner mit Mailadresse am Deal.</p>`}
      <label class="sel wide"><input type="checkbox" name="teams" value="1" ${open.draft.teams ? "checked" : ""}> Teams-Besprechung</label>`;
  }
  return `<div data-record-target='${esc(keyOf(target))}'><div class="recbtns">${btn("note", "Notiz")}${btn("call", "Anruf")}${btn("meeting", "Termin")}${outlook ? btn("outlook", "Termin in Outlook anlegen") : ""}</div>${
    open ? `<form class="fform recform" data-record-form='${esc(keyOf(target))}'>${form}<div class="fbtn"><button class="btn primary">Speichern</button><button class="btn" type="button" data-record-cancel>Abbrechen</button></div></form>` : ""
  }</div>`;
}

/** The form's values as the API takes them; free-text participants are names or addresses. */
export function recordBody(kind: "note" | "call" | "meeting", target: ActivityTarget, f: Record<string, string>, contacts: string[]): NewActivity {
  const at = (s: string | undefined) => (s ? new Date(s).toISOString() : undefined);
  const text = (s: string | undefined) => s?.trim() || null;
  if (kind === "note") return { ...target, type: "note", body: (f.body ?? "").trim(), at: at(f.at) };
  if (kind === "call") return { ...target, type: "call", direction: f.direction === "in" ? "in" : "out", at: at(f.at), durationMin: f.durationMin ? Number(f.durationMin) : null, subject: text(f.subject), body: text(f.body) };
  const others = (f.others ?? "")
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s) ? { email: s } : { name: s.slice(0, 200) }));
  return {
    ...target,
    type: "meeting",
    subject: (f.subject ?? "").trim(),
    at: at(f.at) ?? new Date().toISOString(),
    endsAt: at(f.endsAt) ?? null,
    location: text(f.location),
    body: text(f.body),
    attendees: [...contacts.map((contactId) => ({ contactId })), ...others],
  };
}

const formFields = (f: HTMLFormElement) => {
  const out: Record<string, string> = {};
  for (const [k, v] of new FormData(f).entries()) if (k !== "contact") out[k] = String(v);
  out.contacts = [...f.querySelectorAll<HTMLInputElement>('input[name="contact"]:checked')].map((c) => c.value).join(",");
  return out;
};

/** "Meine Aufgaben" or all open ones, by when they are due. */
export function tasksHtml(tasks: Task[] | null, scope: "mine" | "all"): string {
  const head = `<div class="brief-head"><span class="eyebrow">Aufgaben</span><h1>${scope === "mine" ? "Meine Aufgaben" : "Alle offenen Aufgaben"}</h1>
    <p>Überfälliges zuerst, dann heute, diese Woche und später. Jede Aufgabe führt zu ihrem Deal, ihrer Firma oder Anfrage; die früheste offene Aufgabe eines Deals ist sein nächster Schritt.</p></div>
    <div class="filters"><div class="seg" role="group" aria-label="Aufgaben"><button type="button" data-task-scope="mine" aria-pressed="${scope === "mine"}">Meine</button><button type="button" data-task-scope="all" aria-pressed="${scope === "all"}">Alle offenen</button></div></div>`;
  if (!tasks) return head + emptyState("Lade Aufgaben …", "");
  if (!tasks.length) return head + emptyState(scope === "mine" ? "Keine offenen Aufgaben" : "Keine offenen Aufgaben", scope === "mine" ? "Aufgaben, die dir zugewiesen sind, erscheinen hier." : "Offene Aufgaben an Deals, Firmen und Anfragen erscheinen hier.");
  const groups = (["overdue", "today", "week", "later"] as const)
    .map((g) => [g, tasks.filter((t) => taskGroup(t.due) === g)] as const)
    .filter(([, list]) => list.length)
    .map(
      ([g, list]) =>
        `<div class="sec"><h3 class="${g === "overdue" ? "over" : ""}">${GROUP_TITLE[g]} <small>${list.length}</small></h3><ul class="tasks">${list
          .map((t) => taskRow(t, { where: true, edit: scope === "mine" }))
          .join("")}</ul></div>`,
    )
    .join("");
  return head + groups;
}

export function renderAufgaben(): void {
  $("#v-aufgaben").innerHTML = tasksHtml(S.tasks, S.tasksScope);
}

/** The task of a row, as listed on the page it sits on. */
function taskOf(el: Element): { id: string; version: number } | null {
  const li = el.closest<HTMLElement>("li[data-task]");
  return li ? { id: li.dataset.task!, version: Number(li.dataset.version) } : null;
}

/** Event handlers of all task lists and record forms, registered once. */
export function bindTasks(): void {
  document.addEventListener("click", (e) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>("[data-task-done], [data-task-scope], [data-record], [data-record-cancel]");
    if (!t) return;
    if (t.dataset.taskScope) {
      S.tasksScope = t.dataset.taskScope as "mine" | "all";
      S.tasks = null;
      render();
      return void loadTasks().then(render, () => toast("Die Aufgaben konnten nicht geladen werden."));
    }
    if (t.dataset.record) {
      const box = t.closest<HTMLElement>("[data-record-target]");
      if (!box) return;
      const kind = t.dataset.record as RecordKind;
      const was = recording;
      recording = was && was.key === box.dataset.recordTarget && was.kind === kind ? null : { key: box.dataset.recordTarget!, kind, draft: {} };
      return render();
    }
    if (t.dataset.recordCancel !== undefined) {
      recording = null;
      return render();
    }
    const task = taskOf(t);
    if (task) void write(() => S.api.updateTask(task.id, task.version, { done: true }), "Erledigt");
  });
  document.addEventListener("change", (e) => {
    const t = e.target as HTMLInputElement | HTMLSelectElement;
    const task = t.matches("[data-task-due], [data-task-assignee]") ? taskOf(t) : null;
    if (!task) return;
    if (t.matches("[data-task-due]")) void write(() => S.api.updateTask(task.id, task.version, { due: t.value || null }), t.value ? "Fälligkeit geändert" : "Ohne Fälligkeit");
    else void write(() => S.api.updateTask(task.id, task.version, { assigneeId: t.value || null }), "Zuständig geändert");
  });
  // What is typed into a record form survives the redraws of the page.
  document.addEventListener("input", (e) => {
    const f = (e.target as HTMLElement).closest<HTMLFormElement>("form[data-record-form]");
    const r = recording;
    if (f && r && r.key === f.dataset.recordForm) r.draft = formFields(f);
  });
  document.addEventListener("submit", async (e) => {
    const f = e.target as HTMLFormElement;
    if (f.matches("form[data-task-add]")) {
      e.preventDefault();
      const target = JSON.parse(f.dataset.taskAdd!) as ActivityTarget;
      const v = formFields(f);
      if (!v.text?.trim()) return toast("Bitte die Aufgabe eingeben.");
      await write(() => S.api.createActivity({ ...target, type: "task", text: v.text!.trim(), due: v.due || null, assigneeId: v.assigneeId || null }), "Aufgabe angelegt");
      return;
    }
    if (!f.matches("form[data-record-form]") || !recording) return;
    e.preventDefault();
    const target = JSON.parse(f.dataset.recordForm!) as ActivityTarget;
    const v = formFields(f);
    if (recording.kind === "outlook" && "dealId" in target) {
      if (!v.subject?.trim() || !v.at || !v.endsAt) return toast("Bitte Betreff, Beginn und Ende eingeben.");
      const body = { subject: v.subject.trim(), at: new Date(v.at).toISOString(), endsAt: new Date(v.endsAt).toISOString(), location: v.location?.trim() || null, contactIds: (v.contacts ?? "").split(",").filter(Boolean), teams: v.teams === "1" };
      await write(async () => {
        await S.api.createCalendarEvent(target.dealId, body);
        recording = null;
      }, "Der Termin wird in deinem Outlook angelegt.");
      return;
    }
    const body = recordBody(recording.kind as "note" | "call" | "meeting", target, v, (v.contacts ?? "").split(",").filter(Boolean));
    if (body.type === "note" && !body.body) return toast("Bitte die Notiz eingeben.");
    if (body.type === "meeting" && !body.subject) return toast("Bitte einen Betreff eingeben.");
    const kind = recording.kind;
    await write(
      async () => {
        await S.api.createActivity(body);
        recording = null;
      },
      kind === "note" ? "Notiz gespeichert" : kind === "call" ? "Anruf gespeichert" : "Termin gespeichert",
    );
  });
}
